import { memo } from "react";
import { ExternalLinkIcon, PaperclipIcon, PlugIcon, RotateCwIcon } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Bubble, BubbleContent } from "@/components/ui/bubble";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { Marker, MarkerContent } from "@/components/ui/marker";
import { Message, MessageAvatar, MessageContent } from "@/components/ui/message";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from "@/components/ui/message-scroller";
import { Trace, TraceIcon, TraceLine, TraceTitle } from "@/components/ui/trace";
import { AmyAvatar } from "./AmyAvatar";
import { renderMentions } from "./campaignMentions";
import { MarkdownMessage } from "./MarkdownMessage";
import type { AssistantItem, AuthBlock, Block, ChatItem, QuestionBlock, UserItem } from "./projection";
import { AmyQuestionnaire } from "./AmyQuestionnaire";
import { isApproval } from "./asking";
import { AnsweredQuestionLine, QuestionCard } from "./QuestionCard";
import { ReasoningStep, SubagentStep, ToolStep } from "./steps";
import type { ThreadStatus } from "./threadController";

const isPending = (q: QuestionBlock) => q.status === "open" || q.status === "parked";

/** Something in the turn is visibly moving, so no separate "Thinking" line. */
function hasActiveBlock(blocks: Block[]): boolean {
  return blocks.some((b) => {
    switch (b.kind) {
      case "text":
      case "reasoning":
        return b.streaming;
      case "tool":
        return b.status === "running" && !b.awaitingApproval;
      case "subagent":
        return b.status === "running";
      default:
        return false;
    }
  });
}

type Segment =
  | { kind: "steps"; id: string; blocks: Block[] }
  | { kind: "questions"; id: string; questions: QuestionBlock[] }
  | { kind: "block"; block: Block };

/**
 * Consecutive steps (tool calls, reasoning, delegations, settled questions)
 * share one Trace; consecutive open questions share one questionnaire; text,
 * approvals and notices break both — so the turn reads in the order it
 * happened.
 */
function segment(blocks: Block[]): Segment[] {
  const out: Segment[] = [];
  for (const b of blocks) {
    // A settled approval for a call on screen: the call's line says how it went.
    if (b.kind === "question" && !isPending(b) && b.toolCallId) continue;
    const isStep =
      b.kind === "tool" ||
      b.kind === "reasoning" ||
      b.kind === "subagent" ||
      (b.kind === "question" && !isPending(b)) ||
      (b.kind === "auth" && b.outcome !== undefined);
    if (b.kind === "question" && isPending(b) && !isApproval(b)) {
      const last = out[out.length - 1];
      if (last?.kind === "questions") last.questions.push(b);
      else out.push({ kind: "questions", id: `questions:${b.id}`, questions: [b] });
      continue;
    }
    if (!isStep) {
      out.push({ kind: "block", block: b });
      continue;
    }
    const last = out[out.length - 1];
    if (last?.kind === "steps") last.blocks.push(b);
    else out.push({ kind: "steps", id: `steps:${b.id}`, blocks: [b] });
  }
  return out;
}

function StepView({ block, live }: { block: Block; live: boolean }) {
  switch (block.kind) {
    case "tool":
      return <ToolStep tool={block} live={live} />;
    case "reasoning":
      return <ReasoningStep block={block} />;
    case "subagent":
      return <SubagentStep block={block} live={live} />;
    case "question":
      return <AnsweredQuestionLine question={block} />;
    case "auth":
      return (
        <TraceLine status={block.outcome === "authorized" ? undefined : "error"}>
          <TraceIcon>
            <PlugIcon />
          </TraceIcon>
          <TraceTitle>
            {block.challenge?.displayName || block.name}{" "}
            {block.outcome === "authorized" ? "connected" : block.outcome}
          </TraceTitle>
        </TraceLine>
      );
    default:
      return null;
  }
}

/** A connection that needs the user to sign in before Amy can continue. */
function AuthCard({ block }: { block: AuthBlock }) {
  const challenge = block.challenge;
  return (
    <Item variant="outline" size="sm" className="w-full">
      <ItemMedia variant="icon">
        <PlugIcon />
      </ItemMedia>
      <ItemContent className="min-w-0">
        <ItemTitle>Connect {challenge?.displayName || block.name}</ItemTitle>
        <ItemDescription>{challenge?.instructions || block.description}</ItemDescription>
        {challenge?.userCode && (
          <p className="font-mono font-semibold tracking-wider">{challenge.userCode}</p>
        )}
      </ItemContent>
      {challenge?.url && (
        <ItemActions>
          <Button asChild size="sm" variant="outline" scope={false}>
            <a href={challenge.url} target="_blank" rel="noreferrer">
              <ExternalLinkIcon />
              Sign in
            </a>
          </Button>
        </ItemActions>
      )}
    </Item>
  );
}

function ThinkingLine({ reconnecting }: { reconnecting?: boolean }) {
  return (
    <Trace>
      <TraceLine status="running">
        <TraceTitle>{reconnecting ? "Catching up on Amy's reply" : "Thinking"}</TraceTitle>
      </TraceLine>
    </Trace>
  );
}

function AmyRow({ children }: { children: React.ReactNode }) {
  return (
    <Message align="start" className="group">
      <MessageAvatar className="min-w-0 self-start rounded-md bg-transparent">
        <AmyAvatar className="size-7.5 rounded-none border-0" />
      </MessageAvatar>
      <MessageContent className="gap-3 pt-1">{children}</MessageContent>
    </Message>
  );
}

/**
 * One assistant turn. Memoized on the item object — the projection only
 * rebuilds the item an event touched, so a streaming reply never re-renders
 * (or re-parses the markdown of) the rest of the thread.
 */
const AssistantTurn = memo(function AssistantTurn({
  item,
  live,
  thinking,
}: {
  item: AssistantItem;
  /** The turn is streaming right now. */
  live: boolean;
  thinking: false | "thinking" | "reconnecting";
}) {
  const copyText = item.blocks
    .flatMap((b) => (b.kind === "text" ? [b.text] : []))
    .join("\n\n");
  return (
    <AmyRow>
      {segment(item.blocks).map((seg) => {
        if (seg.kind === "questions") {
          return <AmyQuestionnaire key={seg.id} questions={seg.questions} />;
        }
        if (seg.kind === "steps") {
          return (
            <Trace key={seg.id}>
              {seg.blocks.map((b) => (
                <StepView key={b.id} block={b} live={live} />
              ))}
            </Trace>
          );
        }
        const b = seg.block;
        switch (b.kind) {
          case "text":
            return b.text.trim() ? (
              <MarkdownMessage key={b.id} content={b.text} streaming={b.streaming} />
            ) : null;
          case "question":
            return <QuestionCard key={b.id} question={b} />;
          case "auth":
            return <AuthCard key={b.id} block={b} />;
          case "notice":
            return b.tone === "stopped" ? (
              <Marker key={b.id} variant="separator">
                <MarkerContent>{b.text}</MarkerContent>
              </Marker>
            ) : (
              <Alert key={b.id} variant="destructive">
                <AlertDescription>{b.text}</AlertDescription>
              </Alert>
            );
          default:
            return null;
        }
      })}
      {thinking && <ThinkingLine reconnecting={thinking === "reconnecting"} />}
      {!live && copyText && (
        <div className="-mt-2 -ml-1">
          <CopyButton
            textToCopy={copyText}
            buttonText=""
            variant="ghost"
            tooltipText="Copy reply"
            tooltipDelay={1000}
            tooltipSide="left"
            className="size-8 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:text-foreground"
          />
        </div>
      )}
    </AmyRow>
  );
});

const UserTurn = memo(function UserTurn({
  text,
  files,
  failed,
  onRetry,
}: {
  text: string;
  files?: UserItem["files"];
  failed?: boolean;
  onRetry?: () => void;
}) {
  return (
    <Message align="end">
      <MessageContent>
        {files && files.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1.5">
            {files.map((f, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-1 text-xs text-muted-foreground"
              >
                <PaperclipIcon className="size-3" />
                {f.filename || f.mediaType}
              </span>
            ))}
          </div>
        )}
        <Bubble align="end" variant={failed ? "destructive" : "secondary"}>
          {/* Campaign mention tokens render as chips linking to the campaign. */}
          <BubbleContent className="whitespace-pre-wrap">{renderMentions(text)}</BubbleContent>
        </Bubble>
        {failed && (
          <div className="flex items-center justify-end gap-2 text-xs text-destructive">
            Not delivered
            {onRetry && (
              <Button size="sm" variant="ghost" scope={false} className="h-6 px-2 text-xs" onClick={onRetry}>
                <RotateCwIcon />
                Retry
              </Button>
            )}
          </div>
        )}
      </MessageContent>
    </Message>
  );
});

export interface TranscriptProps {
  items: ChatItem[];
  status: ThreadStatus;
  outgoing: { text: string; failed: boolean } | null;
  /** Errors and warnings shown under the last turn. */
  notices: string[];
  onRetry: () => void;
}

/**
 * The thread (AI Elements Chat): turns inside a Message Scroller. The user's
 * newest message is the one scroll anchor, so a new question settles near
 * the top and the reply streams in under it.
 *
 * Row keys are positional (`u:<index>` / `a:<index>`), and the optimistic
 * user row and the "Thinking" placeholder take the keys their confirmed rows
 * will get — so confirmation never remounts a row (which re-fires the anchor
 * scroll).
 */
export function Transcript({ items, status, outgoing, notices, onRetry }: TranscriptProps) {
  const busy = status !== "idle";
  const last = items[items.length - 1];
  const liveItem = busy && !outgoing && last?.kind === "assistant" ? last : null;
  const thinkingKind = status === "reconnecting" ? "reconnecting" : "thinking";
  // The turn hasn't produced a row yet (just sent, or eve only confirmed the
  // message): a standalone placeholder holds its place.
  const placeholder = busy && (outgoing !== null || last?.kind === "user") && !outgoing?.failed;

  let anchorId: string | undefined = outgoing ? `u:${items.length}` : undefined;
  for (let i = items.length - 1; !anchorId && i >= 0; i--) {
    if (items[i]!.kind === "user") anchorId = items[i]!.id;
  }

  return (
    <MessageScrollerProvider autoScroll defaultScrollPosition="end">
      <MessageScroller className="h-auto min-h-0 flex-1">
        <MessageScrollerViewport>
          <MessageScrollerContent aria-busy={busy} className="mx-auto w-full max-w-3xl gap-6 px-4 py-6">
            {items.map((item) => (
              <MessageScrollerItem key={item.id} messageId={item.id} scrollAnchor={item.id === anchorId}>
                {item.kind === "user" ? (
                  <UserTurn text={item.text} files={item.files} />
                ) : (
                  <AssistantTurn
                    item={item}
                    live={item === liveItem}
                    thinking={item === liveItem && !hasActiveBlock(item.blocks) ? thinkingKind : false}
                  />
                )}
              </MessageScrollerItem>
            ))}
            {outgoing && (
              <MessageScrollerItem messageId={`u:${items.length}`} scrollAnchor>
                <UserTurn text={outgoing.text} failed={outgoing.failed} onRetry={onRetry} />
              </MessageScrollerItem>
            )}
            {placeholder && (
              <MessageScrollerItem messageId={`a:${items.length + (outgoing ? 1 : 0)}`}>
                <AmyRow>
                  <ThinkingLine reconnecting={status === "reconnecting"} />
                </AmyRow>
              </MessageScrollerItem>
            )}
            {notices.length > 0 && (
              <MessageScrollerItem messageId="notices">
                <div className="flex flex-col gap-2">
                  {notices.map((n) => (
                    <Alert key={n} variant="destructive">
                      <AlertDescription>{n}</AlertDescription>
                    </Alert>
                  ))}
                </div>
              </MessageScrollerItem>
            )}
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton variant="neutral" className="rounded-pill shadow-lg" />
      </MessageScroller>
    </MessageScrollerProvider>
  );
}
